import * as React from 'react';
import { useState } from 'react';
import { Button, makeStyles, tokens, Tooltip } from '@fluentui/react-components';
import { ChatInput } from '@fluentui-copilot/react-copilot';
import { AiGeneratedDisclaimer } from '@fluentui-copilot/react-copilot-chat';
import { ChatAttachments } from './FileUpload';
import { COPILOT_CONSTANTS, DEFAULT_VALUES } from './constants';
import type { FooterMessageBoxProps } from './interfaces';
import { Add20Regular, Attach24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  footer: {
    padding: '0',
    backgroundColor: 'transparent',
    flexShrink: 0,
    '& > div': {
      borderTop: 'none !important',
      borderBottom: 'none !important'
    },
    '&.loading': {
      '& button[type="submit"], & [role="button"]': {
        opacity: '0.5',
        cursor: 'not-allowed',
        pointerEvents: 'none'
      }
    }
  },
  chatInputRoot: {
    '& .fai-ChatInput__inputWrapper': {
      display: 'grid !important',
      gridTemplateColumns: '1fr auto',
      gridTemplateRows: 'auto auto'
    },
    '& .fai-ChatInput__actions': {
      gridColumn: '1 / -1',
      gridRow: '2',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      flexWrap: 'nowrap',
      paddingInlineStart: '0 !important',
      paddingLeft: '0 !important',
      borderLeft: 'none !important',
      minHeight: '40px'
    },
    '& .fai-ChatInput__actions > div:first-child': {
      order: -1,
      marginRight: 'auto'
    },
    '& .fai-ChatInput__send[disabled]': {
      backgroundColor: 'transparent !important',
      '&:hover': {
        backgroundColor: 'transparent !important'
      },
      '&:focus': {
        backgroundColor: 'transparent !important'
      }
    }
  },
  actionsRowContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'center',
    width: '100%',
    minHeight: '40px',
    padding: '0',
    gap: '8px'
  },
  actionsLeft: {
    gridColumn: '1',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
    justifyContent: 'flex-start'
  },
  actionsRight: {
    gridColumn: '3',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    justifyContent: 'flex-end'
  },
  counter: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    lineHeight: 1
  },
  aiGenerate: {
    backgroundColor: 'none'
  },
  innerContentDialog: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0px 0px 0px',
    gap: '16px'
  },
  '& .fai-ChatInput__actions > *:first-child': {
    marginLeft: '0 !important'
   }
});

const FooterMessageBox: React.FC<FooterMessageBoxProps> = ({
  controlRef,
  handleSendMessage,
  placeholderText = COPILOT_CONSTANTS.MESSAGES.FALLBACK_PLACEHOLDER,
  isLoading = false,
  maxCount = DEFAULT_VALUES.MAX_COUNT,
  additionalActions,
  hasUploadedFiles = false,
  uploadedFiles = [],
  onFileRemove,
  onFilesChange,
}) => {
  const styles = useStyles();
  const [input, setInput] = React.useState('');
  const [inputKey, setInputKey] = React.useState(0);
  const [ariaLiveMessage, setAriaLiveMessage] = useState('');
  const chatInputRef = React.useRef<HTMLDivElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const count = input.length;
  
  // Generate unique IDs to avoid ARIA conflicts
  const uniqueId = React.useMemo(() => `copilot-input-${Math.random().toString(36).slice(2)}`, []);
  const ariaLiveId = React.useMemo(() => `aria-live-${Math.random().toString(36).slice(2)}`, []);
  const fileInputId = React.useMemo(() => `file-input-${Math.random().toString(36).slice(2)}`, []);

  React.useImperativeHandle(controlRef, () => ({
    setInputText: (text: string) => {
      setInput(text);
      setInputKey((prev) => prev + 1);
    },
    focus: () => {
      setTimeout(() => {
        if (chatInputRef.current) {
          const textarea = chatInputRef.current.querySelector('textarea') || 
                          chatInputRef.current.querySelector('[role="textbox"]') ||
                          chatInputRef.current.querySelector('.fai-EditorInput textarea') ||
                          chatInputRef.current.querySelector('.fai-EditorInput [role="textbox"]');
          
          if (textarea instanceof HTMLElement) {
            textarea.focus();
          } else {
            chatInputRef.current.focus();
          }
        }
      }, 50);
    }
  }));

  const handleSubmit = () => {
    const trimmed = input.trim();
    if ((trimmed || hasUploadedFiles) && !isLoading) {
      handleSendMessage(trimmed);
      setInput('');
      setInputKey((prev) => prev + 1);
    }
  };

  const openFilePicker = () => fileInputRef.current?.click();
  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0 && onFilesChange) {
      // Combine with existing files
      const newFiles = [...uploadedFiles, ...files];
      onFilesChange(newFiles);
    }
    // Clear the input for next selection
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className={`${styles.footer} ${isLoading ? 'loading' : ''}`}>
      <label htmlFor={fileInputId} style={{ display: 'none' }}>
        Upload files for copilot
      </label>
      <input 
        ref={fileInputRef} 
        type="file" 
        hidden 
        multiple 
        onChange={handleFilesSelected}
        id={fileInputId}
        aria-label="Upload files for copilot"
        title="Select files to upload"
      />
      <label htmlFor={uniqueId} style={{ display: 'none' }}>
        MCAPSHelp Copilot message input
      </label>
      <ChatInput
        key={inputKey}
        id={uniqueId}
        aria-label="Type your message to MCAPSHelp Copilot"
        aria-describedby={ariaLiveId}
        className={`${styles.chatInputRoot} textarea-chat`}
        placeholderValue={placeholderText}
        title="Enter your question or request"
        //placeholder={placeholderText}
        defaultValue={input}
        onChange={(_, data) => setInput(data.value)}
        onSubmit={handleSubmit}
        maxLength={maxCount}
        showCount={false}
        appearance="multi"
        send={{ 
          role: 'button', 
          'aria-label': 'Send message to copilot', 
          title: 'Send message',
          disabled: !input.trim() && !hasUploadedFiles
        }}
        style={{ maxWidth: 'none', maxHeight: '120px',borderRadius: "12px",
                fontSize: "16px",
                lineHeight: "24px",
                marginRight: 0, }}
        charactersRemainingMessage={(v) => `${v} characters remaining`}
        ref={chatInputRef as any}
        role="textbox"        
        actions={
          <div className={styles.actionsRowContainer}>
            
            <div className={styles.actionsLeft}>
              <span className={styles.counter}>
                {count}/{maxCount}
              </span>
            </div>
            
            <div className={styles.actionsRight}>
              
              {COPILOT_CONSTANTS.FEATURES.SHOW_ATTACHMENT && (
                <>
                  <ChatAttachments
                    uploadedFiles={uploadedFiles}
                    onFileRemove={onFileRemove}
                    maxVisibleAttachments={1}
                  />
                  <Tooltip content="Add attachments" relationship="label">
                    <Button
                      aria-label="Add attachment files to your message"
                      title="Add attachment files"
                      shape="circular"
                      appearance="transparent"
                      icon={<Attach24Regular />}
                      onClick={openFilePicker}
                    />
                  </Tooltip>
                </>
              )}
              {additionalActions}
            </div>
          </div>
        }
      />
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        id={ariaLiveId}
      >
        {ariaLiveMessage}
      </div>
      <div className={styles.innerContentDialog}>
        <AiGeneratedDisclaimer className={styles.aiGenerate}>
          {COPILOT_CONSTANTS.MESSAGES.DISCLAIMER}
        </AiGeneratedDisclaimer>
        <AiGeneratedDisclaimer className={styles.aiGenerate}>
          {COPILOT_CONSTANTS.MESSAGES.INTERNAL_ONLY}
        </AiGeneratedDisclaimer>
      </div>
    </div>
  );
};

export default FooterMessageBox;
